import React from 'react';
import { Box, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';

/**
 * FunnelWidget - Renders a funnel chart (horizontal bar chart with decreasing values)
 * 
 * @param {Array} data - Array of data points with xLabel and yValue (should be in funnel order)
 * @param {Object} config - Widget configuration (title, description, etc.)
 */
export default function FunnelWidget({ data, config, height = 300 }) {
  
  // Parse limit from widgetType (e.g., "FUNNEL-TOP5")
  let limit = data.length;
  let isTopN = false;
  if (config?.widgetType) {
    // Case insensitive, allow loose matching
    const match = config.widgetType.match(/-TOP\s*(\d+)/i);
    if (match) {
      limit = parseInt(match[1], 10);
      isTopN = true;
    }
  }

  // Also allow direct override if needed
  if (config?.limit) {
    limit = config.limit;
    isTopN = true;
  }

  // Aggregate data by label to avoid duplicates and sum values
  const aggregatedData = data.reduce((acc, item) => {
    // Handle potential case mismatches
    const label = item.xLabel || item.XLabel;
    const value = parseFloat(item.yValue || item.YValue || 0);

    if (!acc[label]) {
      acc[label] = { xLabel: label, yValue: 0 };
    }
    acc[label].yValue += value;
    return acc;
  }, {});

  const processedData = Object.values(aggregatedData);

  // Sort data descending by value safely
  const sortedData = processedData.sort((a, b) => {
    const valA = parseFloat(a.yValue);
    const valB = parseFloat(b.yValue);
    // Handle invalid numbers by pushing them to the end
    if (isNaN(valA) && isNaN(valB)) return 0;
    if (isNaN(valA)) return 1;
    if (isNaN(valB)) return -1;
    return valB - valA;
  });



  const displayData = sortedData.slice(0, limit);
  

  // Transform data for funnel visualization
  const labels = displayData.map(item => item.xLabel);
  const values = displayData.map(item => parseFloat(item.yValue));
  
  // Calculate percentages for funnel effect
  const maxValue = Math.max(...values);
  const percentages = values.map(v => ((v / maxValue) * 100).toFixed(1));

  // Calculate dynamic height for scrolling
  const itemHeight = 40; // Height per bar
  const axisHeight = 30; // Reserved space for axis
  const chartAreaHeight = height - axisHeight;
  const minHeight = chartAreaHeight; 
  const dynamicHeight = Math.max(minHeight, displayData.length * itemHeight);
  
  // Calculate max value for domain synchronization
  // Ensure we have a valid max value, default to 100 if no data
  const dataMax = Math.max(...values, 0);
  const domainMax = dataMax === 0 ? 100 : dataMax;
  
  const needsScroll = dynamicHeight > chartAreaHeight;
  const scrollbarWidth = 10; // Approximate width to align axis

  // Common x-axis configuration to ensure alignment
  const xAxisConfig = [{ 
    min: 0, 
    max: domainMax,
  }];

  return (
    <Box>
      <Box sx={{ height: chartAreaHeight, overflowY: 'auto', pr: 1, overflowX: 'hidden' }}>
        <BarChart
          layout="horizontal"
          yAxis={[{ 
            scaleType: 'band', 
            data: labels 
          }]}
          xAxis={xAxisConfig}
          bottomAxis={null}
          slotProps={{ legend: { hidden: true } }}
          series={[
            {
              data: values,
              label: 'Value',
              color: (() => {
                try {
                  if (config?.metadata) {
                    const meta = JSON.parse(config.metadata);
                    if (meta.color) return meta.color;
                  }
                } catch (e) {}
                return '#ed6c02';
              })(),
            },
          ]}
          height={dynamicHeight}
          margin={{ top: 10, right: 10, bottom: 0, left: 120 }}
        />
      </Box>
      <Box sx={{ height: axisHeight, pr: 1 + (needsScroll ? (scrollbarWidth/8) : 0) }}>
         {/* Dummy Chart just for the Axis */}
        <BarChart
          layout="horizontal"
          xAxis={xAxisConfig}
          yAxis={[{ scaleType: 'band', data: [''] }]}
          leftAxis={null}
          slotProps={{ legend: { hidden: true } }}
          series={[{ data: [0], color: 'transparent' }]}
          height={axisHeight + 30} // Add some buffer for ticks
          margin={{ top: 0, right: 10, bottom: 30, left: 120 }}
        />
      </Box>
      
      {!isTopN && (
        <Box mt={1} display="flex" justifyContent="space-around" flexWrap="wrap">
          {displayData.map((item, index) => (
            <Box key={index} textAlign="center" m={0.5}>
              <Typography variant="caption" color="text.secondary">
                {item.xLabel}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {percentages[index]}%
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
